import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { AccountLayout, TOKEN_PROGRAM_ID, u64 } from "@solana/spl-token";
import {
    AccountInfo,
    Connection,
    EpochInfo,
    GetProgramAccountsFilter,
    KeyedAccountInfo,
    ParsedAccountData,
    PublicKey
} from "@solana/web3.js";
import {
    MARKETS_API,
    RPC_URL,
    WRAPPED_SOL_MINT,
    filterTruthy,
    lamportsToUiAmount,
    stringLamportsToUiAmountDecimal
} from "@bridgesplit/utils";
import axios from "axios";

const TOKEN_2022_PROGRAM_ID = new PublicKey("TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb");
const USER_TOKENS_ROUTE = "/user/tokens";
const USER_WALLET_HEADER = "User-Wallet";

type HttpToken = {
    mint: string;
    tokenAccountAddress: string;
    tokenAmount: {
        amount: string;
        decimals: number;
        uiAmount: number;
        uiAmountString: string;
    };
};

type UserTokensHttpResponse = {
    tokens: HttpToken[];
};

export type TokenAmount = {
    amount: string;
    decimals: number;
    uiAmount: number | null;
    uiAmountString: string;
};

export type UserTokenAccount = {
    address: string;
    mint: string;
    tokenAmount: TokenAmount;
};

type ConnectionWithInternalWebSocket = Connection & {
    _rpcWebSocket?: {
        setAutoReconnect?: (value: boolean) => void;
        setMaxReconnects?: (value: number) => void;
    };
};

function disableWebsocketRetries(connection: Connection) {
    const internalConnection = connection as ConnectionWithInternalWebSocket;
    const rpcWebSocket = internalConnection._rpcWebSocket;

    rpcWebSocket?.setAutoReconnect?.(false);
    rpcWebSocket?.setMaxReconnects?.(0);
}

function mapHttpTokenToUserTokenAccount(token: HttpToken): UserTokenAccount {
    return {
        address: token.tokenAccountAddress,
        mint: token.mint,
        tokenAmount: {
            amount: token.tokenAmount.amount,
            decimals: token.tokenAmount.decimals,
            uiAmount: token.tokenAmount.uiAmount,
            uiAmountString: token.tokenAmount.uiAmountString
        }
    };
}

export const userTokensRestApi = createApi({
    reducerPath: "userTokensRestApi",
    baseQuery: fetchBaseQuery({ baseUrl: MARKETS_API }),
    endpoints: (builder) => ({
        userTokens: builder.query<UserTokenAccount[], string>({
            query: (walletAddress) => ({
                url: USER_TOKENS_ROUTE,
                method: "GET",
                headers: {
                    [USER_WALLET_HEADER]: walletAddress
                }
            }),
            transformResponse: (response: UserTokensHttpResponse) =>
                response.tokens.map((token) => mapHttpTokenToUserTokenAccount(token))
        })
    })
});

function createRpcConnection() {
    const connection = new Connection(RPC_URL, "processed");
    disableWebsocketRetries(connection);
    return connection;
}

function createQueryError(error: unknown): FetchBaseQueryError {
    return {
        status: "CUSTOM_ERROR",
        error: error instanceof Error ? error.message : String(error)
    };
}

function buildWrappedSolAccount(lamports: number, owner: PublicKey): UserTokenAccount {
    const amountString = lamports.toString();
    const decimals = 9;

    return {
        address: owner.toBase58(),
        mint: WRAPPED_SOL_MINT,
        tokenAmount: {
            amount: amountString,
            decimals,
            uiAmount: lamportsToUiAmount(lamports, decimals),
            uiAmountString: stringLamportsToUiAmountDecimal(amountString, decimals)
        }
    };
}

function mapParsedTokenAccount(
    accountInfo: AccountInfo<ParsedAccountData | Buffer>,
    pubkey: PublicKey
): UserTokenAccount | null {
    const data = accountInfo.data;
    if (!("parsed" in data)) return null;

    const parsedData = data as ParsedAccountData;
    const info = parsedData.parsed?.info as
        | {
              mint: string;
              tokenAmount: TokenAmount & {
                  uiAmount?: number | null;
                  uiAmountString?: string;
                  amount?: string;
                  decimals?: number;
              };
          }
        | undefined;

    if (!info?.mint || !info.tokenAmount) return null;
    if (info.mint === WRAPPED_SOL_MINT) return null;

    const tokenAmountInfo = info.tokenAmount;
    const uiAmount =
        typeof tokenAmountInfo.uiAmount === "number" || tokenAmountInfo.uiAmount === null
            ? tokenAmountInfo.uiAmount
            : null;
    const uiAmountString =
        typeof tokenAmountInfo.uiAmountString === "string"
            ? tokenAmountInfo.uiAmountString
            : uiAmount !== null && uiAmount !== undefined
            ? uiAmount.toString()
            : tokenAmountInfo.amount ?? "0";

    const decimals = tokenAmountInfo.decimals ?? 0;
    const owner = accountInfo.owner;
    const programId = owner instanceof PublicKey ? owner.toBase58() : typeof owner === "string" ? owner : null;

    if (!programId) return null;

    return {
        address: pubkey.toBase58(),
        mint: info.mint,
        tokenAmount: {
            amount: tokenAmountInfo.amount ?? "0",
            decimals,
            uiAmount,
            uiAmountString
        }
    };
}

async function fetchTokenAccounts(
    connection: Connection,
    ownerPublicKey: PublicKey
): Promise<UserTokenAccount[] | null> {
    const programIds = [TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID];
    let hasSuccessfulRpcFetch = false;

    const accountEntries: { account: AccountInfo<ParsedAccountData>; pubkey: PublicKey }[] = [];

    for (const programId of programIds) {
        try {
            const response = await axios.post(
                connection.rpcEndpoint,
                {
                    jsonrpc: "2.0",
                    id: 1,
                    method: "getTokenAccountsByOwner",
                    params: [ownerPublicKey.toBase58(), { programId: programId.toBase58() }, { encoding: "jsonParsed", commitment: "processed" }]
                },
                {
                    headers: { "Content-Type": "application/json" }
                }
            );

            hasSuccessfulRpcFetch = true;
            accountEntries.push(
                ...response.data.result.value.map(
                    ({ account, pubkey }: { account: AccountInfo<ParsedAccountData>; pubkey: string }) => ({
                        account: account as AccountInfo<ParsedAccountData>,
                        pubkey: new PublicKey(pubkey)
                    })
                )
            );
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Failed to fetch token accounts for program", programId.toBase58(), error);
            throw error;
        }
    }

    if (!hasSuccessfulRpcFetch) {
        return null;
    }

    let data = accountEntries
        .map(({ account, pubkey }) => mapParsedTokenAccount(account, pubkey))
        .filter((token): token is UserTokenAccount => filterTruthy(token));

    try {
        const lamports = await connection.getBalance(ownerPublicKey, "processed");
        const wrappedSolAccount = buildWrappedSolAccount(lamports, ownerPublicKey);
        data = [...data.filter((token) => token.mint !== WRAPPED_SOL_MINT), wrappedSolAccount];
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to fetch SOL balance", error);
    }

    return data;
}

export const rpcApi = createApi({
    reducerPath: "rpcApi",
    baseQuery: fetchBaseQuery(),
    endpoints: (builder) => ({
        currentEpoch: builder.query<EpochInfo, string>({
            queryFn: async (rpcUrl) => {
                try {
                    const connection = new Connection(rpcUrl);
                    const epoch = await connection.getEpochInfo();
                    return { data: epoch };
                } catch (error) {
                    return { error: createQueryError(error) };
                }
            }
        }),
        tokenAccountsByOwner: builder.query<UserTokenAccount[], string>({
            queryFn: async (walletAddress, { dispatch }) => {
                try {
                    const connection = createRpcConnection();
                    const ownerPublicKey = new PublicKey(walletAddress);
                    const rpcData = await fetchTokenAccounts(connection, ownerPublicKey);

                    if (rpcData) {
                        return { data: rpcData };
                    } else {
                        throw new Error("No data returned from RPC");
                    }
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error("Failed to fetch token accounts from RPC", error);
                }

                const fallbackQuery = dispatch(
                    userTokensRestApi.endpoints.userTokens.initiate(walletAddress, {
                        forceRefetch: true
                    })
                );

                try {
                    const fallbackData = await fallbackQuery.unwrap();
                    return { data: fallbackData ?? [] };
                } catch (fallbackError) {
                    return { error: createQueryError(fallbackError) };
                } finally {
                    fallbackQuery.unsubscribe?.();
                }
            },
            async onCacheEntryAdded(walletAddress, { cacheDataLoaded, cacheEntryRemoved, updateCachedData }) {
                try {
                    await cacheDataLoaded;
                } catch {
                    // ensure we still clean up listeners if the initial load fails
                }

                const connection = createRpcConnection();
                const ownerPublicKey = new PublicKey(walletAddress);
                const pendingParsedAccounts = new Set<string>();
                let isActive = true;

                const loadParsedTokenAccount = async (accountId: PublicKey) => {
                    const address = accountId.toBase58();
                    if (pendingParsedAccounts.has(address)) return;
                    pendingParsedAccounts.add(address);

                    try {
                        const response = await connection.getParsedAccountInfo(accountId, "processed");
                        if (!isActive) return;

                        const value = response.value as AccountInfo<ParsedAccountData | Buffer> | null;
                        const mappedAccount = value ? mapParsedTokenAccount(value, accountId) : null;

                        updateCachedData((draft) => {
                            const existingIndex = draft.findIndex((token) => token.address === address);

                            if (!mappedAccount) {
                                if (existingIndex !== -1) {
                                    draft.splice(existingIndex, 1);
                                }
                                return;
                            }

                            if (existingIndex === -1) {
                                draft.push(mappedAccount);
                            } else {
                                draft[existingIndex] = mappedAccount;
                            }
                        });
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error("Failed to load parsed token account", error);
                    } finally {
                        pendingParsedAccounts.delete(address);
                    }
                };

                const updateWrappedSol = (lamports: number) => {
                    if (!isActive) return;

                    const wrappedSolAccount = buildWrappedSolAccount(lamports, ownerPublicKey);
                    updateCachedData((draft) => {
                        const existingIndex = draft.findIndex((token) => token.mint === WRAPPED_SOL_MINT);
                        if (existingIndex === -1) {
                            draft.push(wrappedSolAccount);
                        } else {
                            draft[existingIndex] = wrappedSolAccount;
                        }
                    });
                };

                let solSubscriptionId: number | undefined;
                try {
                    solSubscriptionId = connection.onAccountChange(
                        ownerPublicKey,
                        (accountInfo) => {
                            if (!isActive) return;
                            updateWrappedSol(accountInfo.lamports);
                        },
                        "processed"
                    );
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error("Failed to subscribe to SOL balance changes", error);
                }

                const filters: GetProgramAccountsFilter[] = [
                    {
                        memcmp: {
                            offset: 32,
                            bytes: walletAddress
                        }
                    }
                ];

                const programIds = [TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID];
                const programSubscriptionIds: number[] = [];

                const handleProgramAccountChange = ({ accountId, accountInfo }: KeyedAccountInfo) => {
                    if (!isActive) return;

                    const address = accountId.toBase58();
                    const accountData = accountInfo.data;

                    if (!(accountData instanceof Uint8Array)) {
                        return;
                    }

                    if (accountData.length === 0) {
                        updateCachedData((draft) => {
                            const index = draft.findIndex((token) => token.address === address);
                            if (index !== -1) {
                                draft.splice(index, 1);
                            }
                        });
                        return;
                    }

                    let shouldLoadParsedAccount = false;

                    updateCachedData((draft) => {
                        const existingIndex = draft.findIndex((token) => token.address === address);
                        const existingAccount = existingIndex === -1 ? undefined : draft[existingIndex];

                        try {
                            const decoded = AccountLayout.decode(accountData);
                            const mint = new PublicKey(decoded.mint).toBase58();
                            const amount = u64.fromBuffer(decoded.amount).toString();
                            const decimals = existingAccount?.tokenAmount.decimals;

                            if (decimals === undefined) {
                                shouldLoadParsedAccount = true;
                                return;
                            }

                            const uiAmountString = stringLamportsToUiAmountDecimal(amount, decimals);
                            const parsedUiAmount = Number.parseFloat(uiAmountString);

                            const owner = accountInfo.owner;
                            const hasValidOwner = owner instanceof PublicKey || typeof owner === "string";

                            if (!hasValidOwner) {
                                return;
                            }

                            const updatedToken: UserTokenAccount = {
                                address,
                                mint,
                                tokenAmount: {
                                    amount,
                                    decimals,
                                    uiAmount: Number.isFinite(parsedUiAmount)
                                        ? parsedUiAmount
                                        : existingAccount?.tokenAmount.uiAmount ?? null,
                                    uiAmountString
                                }
                            };

                            if (existingIndex === -1) {
                                draft.push(updatedToken);
                            } else {
                                draft[existingIndex] = updatedToken;
                            }
                        } catch (error) {
                            // eslint-disable-next-line no-console
                            console.error("Failed to decode token account from buffer", error);
                        }
                    });

                    if (shouldLoadParsedAccount) {
                        void loadParsedTokenAccount(accountId);
                    }
                };

                for (const programId of programIds) {
                    try {
                        const subscriptionId = connection.onProgramAccountChange(
                            programId,
                            handleProgramAccountChange,
                            {
                                commitment: "processed",
                                filters
                            }
                        );
                        programSubscriptionIds.push(subscriptionId);
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error("Failed to subscribe to token account changes", error);
                    }
                }

                try {
                    await cacheEntryRemoved;
                } finally {
                    isActive = false;

                    if (solSubscriptionId !== undefined) {
                        try {
                            await connection.removeAccountChangeListener(solSubscriptionId);
                        } catch (error) {
                            // eslint-disable-next-line no-console
                            console.error("Failed to remove SOL balance subscription", error);
                        }
                    }

                    await Promise.all(
                        programSubscriptionIds.map(async (subscriptionId) => {
                            try {
                                await connection.removeProgramAccountChangeListener(subscriptionId);
                            } catch (error) {
                                // eslint-disable-next-line no-console
                                console.error("Failed to remove token account subscription", error);
                            }
                        })
                    );
                }
            }
        })
    })
});

export const { useCurrentEpochQuery, useTokenAccountsByOwnerQuery, useLazyTokenAccountsByOwnerQuery } = rpcApi;
export const { useUserTokensQuery: useUserTokensHttpQuery } = userTokensRestApi;
